import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  type TemplateRef,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleRight, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { type SelectableOption, type SelectableOptionGroup } from '@reconcept-platform/shared/data-access';
import { OptionListComponent } from '@reconcept-platform/shared/ui/standalone/option-list';

export interface OverlayFilterOptionGroup<T = any> {
  someOptionsSelected?: boolean;
  allOptionsSelected?: boolean;
  label: string;
  value?: T; // the topic itself can also have a value
  children?: SelectableOption<T>[];
  optionGroups?: SelectableOptionGroup<T>[];
  selection?: T[] | undefined; // the selected values
}

@Component({
  selector: 'rcp-overlay-filter-options',
  imports: [CommonModule, FontAwesomeModule, OptionListComponent],
  templateUrl: './overlay-filter-options.component.html',
  styleUrls: ['./overlay-filter-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OverlayFilterOptionsComponent {
  @Input() title = 'Filters';
  @Input() options: OverlayFilterOptionGroup[];
  @Output() filterOptions = new EventEmitter<OverlayFilterOptionGroup[]>();
  @Output() resetFilter = new EventEmitter<void>();

  @ContentChild('topicListItem') topicListItem: TemplateRef<any>;
  @ContentChild('optionListItem') optionListItem: TemplateRef<any>;

  filterActive: boolean;
  selectedOption: OverlayFilterOptionGroup;

  selectOption(filterOption: OverlayFilterOptionGroup) {
    this.setSelectedOption(filterOption);

    if (this.selectedOption.value) {
      this.setAllOptions(filterOption, !this.selectedOption?.allOptionsSelected);
      this.updateFilter(filterOption);
    }
  }

  setAllOptionsSelected(selected: boolean, topic: OverlayFilterOptionGroup) {
    this.setAllOptions(topic, selected);
    topic.someOptionsSelected = false;

    this.setSelectedOption(topic);
    this.updateFilter(topic);
  }

  updateFilter($event: OverlayFilterOptionGroup) {
    this.updateAllOptionsSelected($event);
    this.options.forEach((option) => {
      // @ts-expect-error TS2322 type not assignable
      option.selection = this.getSelection(option);
    });

    this.notifyFilterOptions(this.options);
    this.filterActive = this.options.some((option) => option.someOptionsSelected || option.allOptionsSelected);
  }

  updateAllOptionsSelected(topic: OverlayFilterOptionGroup) {
    topic.allOptionsSelected = topic.value
      ? topic.allOptionsSelected
      : topic.children?.every((option) => option.selected === true);
  }

  updateSelectableGroups($event: OverlayFilterOptionGroup): void {
    const allSelectableOptions = $event.children ?? $event.optionGroups?.map((it) => it.children).flat();
    // @ts-expect-error TS2345 argument not assignable
    $event.allOptionsSelected = this.areAllSelected(allSelectableOptions);
    // @ts-expect-error TS2345 argument not assignable
    $event.someOptionsSelected = this.areSomeOptionsSelected($event, allSelectableOptions);
    this.updateFilter($event);
  }

  doResetFilter() {
    this.setSelectedOption(undefined);
    this.deselectAllOptions();
    this.filterActive = false;

    this.resetFilter.emit();
    this.notifyFilterOptions(this.options);
  }

  defaultSelectOption(selectedOption: OverlayFilterOptionGroup, option: SelectableOption<any>) {
    if (!this.optionListItem) {
      this.deselectAllOptions();
      option.selected = true;
      this.updateSelectableGroups(selectedOption);
    }
  }

  private getSelection(option: OverlayFilterOptionGroup<any>) {
    // get the value from either the Topic, or one of the children or optiongroups
    option.selection =
      !option.children && !option.optionGroups
        ? option.allOptionsSelected && option.value
        : [...(option.children ?? []), ...(option.optionGroups?.map((it) => it.children).flat() ?? [])]
            .filter((it) => it.selected)
            .map((it) => it.value);

    return this.sanitizeSelection(option);
  }

  /**
   * If option is not selected, or the selection array has no values,
   * we need to return undefined to turn off the filter.
   * @param option
   * @private
   */
  private sanitizeSelection(option: OverlayFilterOptionGroup<any>) {
    return Array.isArray(option.selection) && option.selection?.length
      ? option.selection
      : ((!Array.isArray(option.selection) && option.selection) ?? undefined);
  }

  private deselectAllOptions() {
    this.options.forEach((option) => {
      this.setAllOptions(option, false);
      option.selection = undefined;
      option.someOptionsSelected = false;
      option.optionGroups?.forEach((group) => this.setAllChildrenSelected(group.children, false));
    });
  }

  private areAllSelected(options: SelectableOption<string>[]): boolean {
    return options.filter((option) => option.selected === true).length === options.length;
  }

  private areSomeOptionsSelected($event: any, options: SelectableOption<string>[]): boolean {
    return options.filter((option) => option.selected).length > 0 && !$event.allOptionsSelected;
  }

  private setSelectedOption(topic: OverlayFilterOptionGroup | undefined) {
    // @ts-expect-error TS2322 type not assignable
    this.selectedOption = topic;
  }

  private setAllOptions(topic: OverlayFilterOptionGroup, selected: boolean) {
    // @ts-expect-error TS2345 argument not assignable
    this.setAllChildrenSelected(topic.children, selected);
    topic.allOptionsSelected = selected;
  }

  private setAllChildrenSelected(options: SelectableOption[], selected: boolean) {
    options?.forEach((option) => (option.selected = selected));
  }

  private notifyFilterOptions(selectedOptions: OverlayFilterOptionGroup[]) {
    this.filterOptions.emit(selectedOptions.filter((it) => it.selection));
  }

  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faAngleRight = faAngleRight;
}
